<template>
  <nav class="app-menu">
    <ul class="menu-list">
      <li>
        <pu-homepage-link @click="appMenuStore.hideMenu">
          <img
            loading="lazy"
            src="@/assets/images/pitchup-favicon.svg"
            class="menu-icon home"
            width="25"
            height="22"
          />
          <translate class="menu-item-text"> Home </translate>
          <img
            loading="lazy"
            src="@/assets/images/down-rounded.svg"
            class="menu-icon menu-action"
            width="7"
            height="13"
          />
        </pu-homepage-link>
      </li>
    </ul>

    <translate tag="div" class="section-title"> Top destinations </translate>
    <AsyncPopularDestinations
      :key="getAsyncComponentKey('popularDestinations')"
      @locationUrlClicked="appMenuStore.hideMenu"
      @vue:mounted="setAsyncComponentKey('popularDestinations')"
    />

    <AsyncAppMenuInternationalization
      v-if="!error"
      :key="getAsyncComponentKey('internationalization')"
      :total-counts="totalCounts"
      @select="appMenuStore.hideMenu"
      @vue:mounted="setAsyncComponentKey('internationalization')"
    />

    <translate tag="div" class="section-title"> Accommodation type </translate>
    <AsyncAppMenuCategories
      :key="getAsyncComponentKey('categories')"
      @select="appMenuStore.hideMenu"
      @vue:mounted="setAsyncComponentKey('categories')"
    />

    <translate tag="div" class="section-title"> Popular searches </translate>
    <AsyncPopularSearches
      :key="getAsyncComponentKey('popularSearches')"
      :with-image="false"
      class="app-menu-list searches"
      @popular-search-clicked="appMenuStore.hideMenu"
      @vue:mounted="setAsyncComponentKey('popularSearches')"
    />

    <ul class="menu-list">
      <li>
        <a
          :href="`https://www.pitchup.com/${
            $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
          }join/`"
          target="_blank"
        >
          <img
            loading="lazy"
            src="@/assets/homepage/owners-background-icon.png"
            class="menu-icon join-as-owner"
            width="39"
            height="39"
          />
          <translate class="menu-item-text">Join as site owner</translate>
          <img
            loading="lazy"
            src="@/assets/images/down-rounded.svg"
            class="menu-icon menu-action"
          />
        </a>
      </li>
    </ul>

    <ui-collapse
      v-if="['en-gb', 'fr', 'de', 'nl', 'it', 'es'].includes($i18n.locale)"
      :collapse-id="`collapse-national-parks`"
    >
      <template #ui-collapse-title>
        <translate>National parks</translate>
      </template>
      <template #ui-collapse-body>
        <AsyncNationalParks
          :key="getAsyncComponentKey('nationalParks')"
          :with-image="false"
          class="app-menu-list collapse"
          @national-park-clicked="appMenuStore.hideMenu"
          @vue:mounted="setAsyncComponentKey('nationalParks')"
        />
      </template>
    </ui-collapse>

    <ui-collapse
      v-if="shouldShowArticles"
      :collapse-id="`collapse-how-to-s-and-guides`"
    >
      <template #ui-collapse-title>
        <translate>How to's & guides</translate>
      </template>
      <template #ui-collapse-body>
        <AsyncArticleList
          :key="getAsyncComponentKey('articleList')"
          :titles-only="true"
          class="app-menu-list collapse"
          @article-clicked="appMenuStore.hideMenu"
          @vue:mounted="setAsyncComponentKey('articleList')"
        />
      </template>
    </ui-collapse>

    <ui-collapse
      class="hierarchy"
      :selected="locationsOpen"
      data-test="menu-locations-collapse"
      :collapse-id="`collapse-locations`"
      @collapse-open="onLocationsOpen"
      @collapse-close="locationsOpen = false"
    >
      <template #ui-collapse-title>
        <translate tag="div" class="locations"> Locations A - Z </translate>
      </template>
      <template #ui-collapse-body>
        <section class="location-container a-z">
          <div v-if="locationLoading" class="location-spinner">
            <loading-spinner />
          </div>
          <AsyncAppMenuLocations
            :key="getAsyncComponentKey('locations')"
            :is-open="locationsOpen"
            @can-scroll-to="canScrollTo"
            @location-loading="locationLoading = $event"
            @location-url-clicked="locationSelected"
            @vue:mounted="setAsyncComponentKey('locations')"
          />
        </section>
      </template>
    </ui-collapse>

    <translate tag="div" class="section-title"> About us </translate>
    <AsyncAppMenuAboutUs
      :key="getAsyncComponentKey('aboutUs')"
      @vue:mounted="setAsyncComponentKey('aboutUs')"
    />

    <translate tag="div" class="section-title"> Links </translate>
    <ul class="menu-list signin">
      <li v-if="isManager">
        <a
          :href="`/${
            $i18n.locale !== 'en-gb' ? $i18n.locale + '/' : ''
          }supplier2/`"
          target="_blank"
        >
          <translate class="menu-item-text">Manager portal</translate>
          <img
            loading="lazy"
            src="@/assets/images/down-rounded.svg"
            class="menu-icon menu-action"
          />
        </a>
      </li>
      <li v-if="$i18n.locale === 'en-gb'">
        <nuxt-link :to="$puLocalePath({ path: '/guides/' })" target="_blank">
          <span class="menu-item-text">Camping guides</span>
          <img
            loading="lazy"
            src="@/assets/images/down-rounded.svg"
            class="menu-icon menu-action"
          />
        </nuxt-link>
      </li>
      <li v-if="$i18n.locale === 'en-gb'">
        <nuxt-link
          :to="$puLocalePath({ path: '/camping-ideas/' })"
          target="_blank"
        >
          <span class="menu-item-text">Camping ideas</span>
          <img
            loading="lazy"
            src="@/assets/images/down-rounded.svg"
            class="menu-icon menu-action"
          />
        </nuxt-link>
      </li>
      <li v-if="$i18n.locale === 'en-gb'">
        <nuxt-link
          :to="$puLocalePath({ path: '/camping-ideas/best-campsites/' })"
          target="_blank"
        >
          <span class="menu-item-text">Best campsites UK</span>
          <img
            loading="lazy"
            src="@/assets/images/down-rounded.svg"
            class="menu-icon menu-action"
          />
        </nuxt-link>
      </li>
      <li>
        <span class="menu-link" @click="appMenuStore.showAccountMenu">
          <translate v-if="isLoggedIn" class="menu-item-text"
            >My account</translate
          >
          <translate v-else class="menu-item-text"
            >Account sign up & login</translate
          >
          <img
            loading="lazy"
            src="@/assets/images/down-rounded.svg"
            class="menu-icon menu-action"
          />
        </span>
      </li>
      <li>
        <nuxt-link
          :to="$puLocalePath({ path: '/about/resend_confirmation_email/' })"
          @click="appMenuStore.hideMenu"
        >
          <translate class="menu-item-text">
            Resend confirmation email
          </translate>
          <img
            loading="lazy"
            src="@/assets/images/down-rounded.svg"
            class="menu-icon menu-action"
          />
        </nuxt-link>
      </li>
    </ul>

    <AsyncAppMenuSocialLinks
      :key="getAsyncComponentKey('socialLinks')"
      @vue:mounted="setAsyncComponentKey('socialLinks')"
    />

    <br />
    <br />
    <AsyncVersion
      :key="getAsyncComponentKey('version')"
      @vue:mounted="setAsyncComponentKey('version')"
    />
  </nav>
</template>

<script lang="ts">
import AuthMixin from '~/mixins/AuthMixin'
import { defineAsyncComponent } from 'vue'
import { hydrateOnVisibleSafe } from '../../../../utility/hydration/hydrateOnVisibleSafe'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'

const AsyncPopularDestinations = defineAsyncComponent({
  loader: () => import('./PopularDestinations.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAppMenuInternationalization = defineAsyncComponent({
  loader: () => import('./AppMenuInternationalization.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAppMenuCategories = defineAsyncComponent({
  loader: () => import('./AppMenuCategories.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncPopularSearches = defineAsyncComponent({
  loader: () => import('~/components/homepage/PopularSearches.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncNationalParks = defineAsyncComponent({
  loader: () => import('~/apps/national-parks/NationalParks.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncArticleList = defineAsyncComponent({
  loader: () => import('~/components/homepage/ArticleList.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAppMenuLocations = defineAsyncComponent({
  loader: () => import('./locations/AppMenuLocations.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAppMenuAboutUs = defineAsyncComponent({
  loader: () => import('./AppMenuAboutUs.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncAppMenuSocialLinks = defineAsyncComponent({
  loader: () => import('./AppMenuSocialLinks.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncVersion = defineAsyncComponent({
  loader: () => import('../Version.vue'),
  hydrate: hydrateOnVisibleSafe(),
})
import { logger } from '../../../../utility/logger'

@Component({
  components: {
    AsyncPopularDestinations,
    AsyncAppMenuInternationalization,
    AsyncAppMenuCategories,
    AsyncPopularSearches,
    AsyncNationalParks,
    AsyncArticleList,
    AsyncAppMenuLocations,
    AsyncAppMenuAboutUs,
    AsyncAppMenuSocialLinks,
    AsyncVersion,
  },
  setup(props) {
    const dataMenuOpen = computed(() => {
      return props.appMenuIsVisible ? 'true' : 'false'
    })

    useHead({
      bodyAttrs: {
        'data-menu-open': dataMenuOpen,
      },
    })

    const error = useError()
    const appMenuStore = useAppMenuStore(usePinia())

    return { appMenuStore, error }
  },
  mixins: [AuthMixin],
})
class AppMenu extends Vue {
  appMenuStore: ReturnType<typeof useAppMenuStore>
  locationLoading = false
  locationsOpen = false

  asyncComponentKeys: Record<string, string> = {}

  // Helper methods for async component keys
  getAsyncComponentKey(componentName: string): string {
    return (
      this.asyncComponentKeys[componentName] ||
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
    )
  }

  setAsyncComponentKey(componentName: string): void {
    this.asyncComponentKeys[componentName] =
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
  }

  @Prop({ default: false })
  appMenuIsVisible: boolean

  @Prop()
  totalCounts: number

  @Watch('appMenuVisible')
  onAppMenuVisibleChanged(value: boolean) {
    if (!value) {
      this.locationsOpen = false
    }
  }

  async onLocationsOpen() {
    this.locationsOpen = true
    await this.scrollToHierarchy()
  }

  private async scrollToHierarchy() {
    const activeNode = document.querySelector('.active.node') as HTMLElement
    if (activeNode) {
      await this.$nextTick()
      logger('Scroll to activeNode')
      activeNode.scrollIntoView({ block: 'center' })
    }
  }

  locationSelected() {
    this.locationsOpen = false
    this.appMenuStore.hideMenu()
  }

  async canScrollTo() {
    await this.$nextTick()
    if (this.locationsOpen) {
      await this.scrollToHierarchy()
    }
  }

  get shouldShowArticles() {
    return this.$i18n.locale.startsWith('en-')
  }
}

export default toNative(AppMenu)
</script>

<style scoped>
/* mobile only */
.app-menu {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  width: 85vw;
  overflow-y: auto;
  overflow-x: hidden;
  transform: translateX(0);
  visibility: hidden;
}

[data-menu-open='true'] {
  overflow-x: hidden;
}

[data-menu-open='true'] .app-menu {
  visibility: visible;
}

.section-title {
  margin-top: 1em;
  padding: 0.5em 0.5em 0.5em 1em;
  font-weight: bold;
  color: #477e9b;
}

.location-hierarchy {
  justify-content: flex-end;
}

.location-container,
.location-spinner {
  display: flex;
  flex-direction: column;
  position: relative;
}

.location-container.a-z {
  min-height: 45vh;
  position: relative;
}

.location-container.popular {
  min-height: 22vh;
}

.location-container.a-z:after,
.location-container.a-z:before {
  content: '';
  width: 100%;
  position: absolute;
  height: 0.5em;
  display: block;
  z-index: 1;
}

.location-container.a-z:after {
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(57, 74, 90, 0) 0%,
    rgba(57, 74, 90, 0.3) 100%
  );
}

.location-container.a-z:before {
  top: 0;
  background: linear-gradient(
    to bottom,
    rgba(57, 74, 90, 0.3) 0%,
    rgba(57, 74, 90, 0) 100%
  );
}

.hierarchy {
  border-color: var(--colour-table-borders);
}

.hierarchy.expanded {
  min-height: 53vh;
}

.hierarchy :deep(.ui-collapse-content) {
  border: none;
}

.location-spinner {
  position: absolute;
  z-index: 1;
  background: rgba(255, 255, 255, 0.75);
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.menu-icon.home {
  width: 1.5em;
}

.app-menu :deep(.menu-list) {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  border-top: 1px solid var(--colour-table-borders);
  font-size: var(--body);
}

.app-menu :deep(.menu-list:not(.internationalization) li a),
.app-menu :deep(.menu-list .menu-link) {
  font-weight: normal;
  color: #333;
  text-decoration: none;
  padding: 0.2em 1em 0.2em 1.1em;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--colour-table-borders);
  min-height: 2.75em;
}

.app-menu :deep(.menu-list.internationalization) {
  margin-top: 1.5em;
}

.app-menu :deep(.menu-list li) {
  cursor: pointer;
}

.app-menu :deep(.menu-list li:active) {
  background: rgba(0, 0, 0, 0.2);
}

.app-menu :deep(.menu-item-text) {
  flex-grow: 1;
}

.app-menu :deep(.menu-icon) {
  width: 1.7em;
  max-height: 1.7em;
  display: block;
  flex-shrink: 0;
  align-self: center;
  margin-right: 0.8em;
}

.app-menu .menu-icon.join-as-owner {
  width: 2.3em;
  max-height: 2.3em;
  margin-right: 0.4em;
}

.app-menu :deep(.menu-icon.menu-action) {
  transform: rotate(-90deg);
  width: 0.9em;
  opacity: 0.7;
  margin: 0;
}

.app-menu-list {
  display: block;
}

.section-title.recents {
  margin-top: 0;
  min-height: 3.2em;
  display: flex;
  align-items: center;
}

.app-menu :deep(.ui-collapse-header) {
  min-height: 3em;
  padding: 0.2em 0.75em 0.2em 1.1em;
}

.app-menu :deep(.ui-collapse-title) {
  color: #477e9b;
}

.app-menu :deep(.ui-collapse-icon.end) {
  transform: scale(0.7);
}

.app-menu-list.searches {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(6em, max-content)) !important;
  column-gap: 1em;
  justify-content: space-between;
  align-items: center;
  padding: 0 1em 1.5em 1em;
  row-gap: 0.5em;
  font-size: var(--body);
  grid-auto-rows: unset;
  border-bottom: 1px solid var(--colour-page-background);
}

.app-menu-list.searches :deep(a) {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  line-height: 1.1;
  background: #e1e6ef;
  border-radius: 0.5em;
  padding: 0.4em 0.5em;
}

.app-menu-list.collapse :deep(a) {
  padding: 0.75em 1.1em;
  border-bottom: 1px solid var(--colour-page-background);
}

.app-menu-list.collapse :deep(a .name),
.app-menu-list.collapse :deep(a .title) {
  color: #333;
  font-weight: normal;
}
</style>
